<template>
  <div class="event-dashboard-container">
    <!-- Event Header Banner -->
    <div class="event-header-section">
      <div v-if="loadingEvent" class="event-header-card" style="display: flex; justify-content: center; align-items: center; min-height: 100px;">
        <font-awesome-icon icon="running" class="fa-3x running-animate" spin />
        <span style="margin-left: 1rem; font-size: 1.2rem; font-weight: 600;">{{$t('eventDashboard.loadingAthletes')}}</span>
      </div>
      <div v-else class="event-header-card">
        <div class="event-header-content">
          <div class="event-basic-info">
            <div class="event-type-badge">
              <font-awesome-icon icon="swimmer" v-if="event.type==='Swim'" class="type-icon swim" />
              <font-awesome-icon icon="biking" v-if="event.type==='Ride'" class="type-icon bike" />
              <font-awesome-icon icon="running" v-if="event.type==='Run'" class="type-icon run" />
              <font-awesome-icon icon="walking" v-if="event.type==='Walk'" class="type-icon walk" />
              <span v-for="(type, idx) in (event.type || [])" :key="idx" class="type-icon-wrapper">
                <font-awesome-icon icon="swimmer" v-if="type=='Swim'" class="type-icon swim" />
                <font-awesome-icon icon="biking" v-if="type=='Ride'" class="type-icon bike" />
                <font-awesome-icon icon="running" v-if="type=='Run'" class="type-icon run" />
                <font-awesome-icon icon="walking" v-if="type=='Walk'" class="type-icon walk" />
              </span>
            </div>
            <div class="event-meta">
              <div class="event-dates">
                <font-awesome-icon icon="calendar-alt" class="meta-icon" />
                <span>{{ event.from * 1000 | formatTimestamp }} - {{ event.to * 1000 | formatTimestamp }}</span>
              </div>
              <div class="event-status">
                <!-- Desktop status badges with text -->
                <span v-if="isUpcomingEvent(event.from*1000)" class="status-badge upcoming hide-on-mobile">
                  <font-awesome-icon icon="clock" class="status-icon" />
                  {{ $t('eventDashboard.statusUpcoming') }}
                </span>
                <span v-if="isExpiredEvent(event.to*1000)" class="status-badge expired hide-on-mobile">
                  <font-awesome-icon icon="calendar-check" class="status-icon" />
                  {{ $t('eventDashboard.statusExpired') }}
                </span>
                <span v-if="!isUpcomingEvent(event.from*1000) && !isExpiredEvent(event.to*1000)" class="status-badge active hide-on-mobile">
                  <font-awesome-icon icon="play-circle" class="status-icon" />
                  {{ $t('eventDashboard.statusActive') }}
                </span>

                <!-- Mobile status badges with icons only -->
                <span v-if="isUpcomingEvent(event.from*1000)" class="mobile-status-badge upcoming show-on-mobile">
                  <font-awesome-icon icon="clock" class="mobile-status-icon" />
                </span>
                <span v-if="isExpiredEvent(event.to*1000)" class="mobile-status-badge expired show-on-mobile">
                  <font-awesome-icon icon="calendar-check" class="mobile-status-icon" />
                </span>
                <span v-if="!isUpcomingEvent(event.from*1000) && !isExpiredEvent(event.to*1000)" class="mobile-status-badge active show-on-mobile">
                  <font-awesome-icon icon="play-circle" class="mobile-status-icon" />
                </span>
              </div>
            </div>
          </div>
          <div class="event-right-section">
            <div class="event-image">
              <img :src="event.image" :alt="event.name" class="event-thumbnail">
            </div>
            <div class="event-actions">
              <router-link :to="`/event-var/${event.slug}`" class="var-btn">
                <font-awesome-icon icon="chart-line" class="var-icon" />
                <span>Var</span>
              </router-link>
              <router-link :to="`/event-map/${event.slug}`" class="map-btn">
                <font-awesome-icon icon="map-marked-alt" class="map-icon" />
                <span>Map</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Filter Toggle Button -->
    <button
      class="mobile-filter-toggle-btn"
      v-if="isMobile"
      @click="showMobileFilters = !showMobileFilters"
      aria-controls="filters-section"
      :aria-expanded="showMobileFilters.toString()"
    >
      <font-awesome-icon icon="sliders-h" class="filters-icon compact-icon" />
      <span class="toggle-label">{{ $t('eventDashboard.filter') }}</span>
      <font-awesome-icon
        :icon="showMobileFilters ? 'chevron-up' : 'chevron-down'"
        class="chevron-icon"
        :class="{ rotated: showMobileFilters }"
      />
    </button>

    <!-- Filters Section -->
    <transition name="filters-fade-slide">
      <div
        v-show="!isMobile || showMobileFilters"
        class="filters-section"
        id="filters-section"
      >
        <div class="filters-card compact-filters-card" style="padding: 0.5em 0.7em;">
          <div class="filters-content compact-filters-content" style="display: flex; align-items: center; gap: 0.5em; flex-wrap: wrap; padding: 0;">
            <!-- Gender Filter (if levels) -->
            <div v-if="event.levels" style="display: flex; flex-direction: row; align-items: center; gap: 0.3em;">
              <button
                @click="filter('')"
                class="filter-btn overall-btn compact-filter-btn"
                :class="{ active: !currentGenderFilter }"
                :title="$t('eventDashboard.overall')"
                style="padding: 0.3em 0.6em; min-width: 32px;"
              >
                <font-awesome-icon icon="users" />
              </button>
              <button
                @click="filter('F')"
                class="filter-btn female-btn compact-filter-btn"
                :class="{ active: currentGenderFilter === 'F' }"
                :title="$t('eventDashboard.female')"
                style="padding: 0.3em 0.6em; min-width: 32px;"
              >
                <font-awesome-icon icon="venus" />
              </button>
              <button
                @click="filter('M')"
                class="filter-btn male-btn compact-filter-btn"
                :class="{ active: currentGenderFilter === 'M' }"
                :title="$t('eventDashboard.male')"
                style="padding: 0.3em 0.6em; min-width: 32px;"
              >
                <font-awesome-icon icon="mars" />
              </button>
            </div>

            <!-- Team Filter (if teams) -->
            <template v-if="event.teams">
              <select v-model="filterByTeam" class="team-select compact-team-select" :title="$t('eventDashboard.team')" style="min-width: 90px; font-size: 0.95em; padding: 0.3em 0.5em;">
                <option value="">{{ $t('eventDashboard.team') }} (All)</option>
                <option v-for="(option, idx) in (event.teams || [])" :key="idx" :value="option.slug">
                  {{ option.name }}
                </option>
              </select>
            </template>

            <!-- Reset Filters Button -->
            <button
              v-if="hasActiveFilters"
              @click="resetAllFilters"
              class="reset-btn compact-reset-btn"
              :title="$t('eventDashboard.resetFilters')"
              style="padding: 0.3em 0.6em; min-width: 32px;"
            >
              <font-awesome-icon icon="undo" />
            </button>

            <!-- Search -->
            <div class="search-wrapper compact-search-wrapper" style="display: flex; align-items: center; gap: 0.2em; margin-left: auto;">
              <input
                v-model="athleteNameSearchInput"
                :placeholder="$t('eventDashboard.searchPlaceholder')"
                class="search-input compact-search-input"
                style="font-size: 0.95em; padding: 0.3em 0.5em; min-width: 90px;"
              />
              <button @click="searchByName(athleteNameSearchInput)" class="search-btn compact-search-btn" :title="$t('eventDashboard.search')" style="padding: 0.3em 0.6em; min-width: 32px;">
                <font-awesome-icon icon="search" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Results Section -->
    <div class="results-section" :class="tableBgByGender">
      <div :class="['results-card', tableBgByGender]">
        <div class="results-header">
          <h2 class="results-title">
            <font-awesome-icon icon="list-ol" class="results-icon" />
            {{ $t('eventDashboard.ranking') }}
          </h2>
          <div class="results-info">
            <span class="total-participants">
              <font-awesome-icon icon="users" class="info-icon" />
              {{ $t('eventDashboard.participants', { count: paginator.count }) }}
            </span>
            <span v-if="currentGenderFilter" :class="['current-gender', currentGenderFilter === 'F' ? 'female-filter' : 'male-filter']">
              <font-awesome-icon :icon="currentGenderFilter === 'F' ? 'venus' : 'mars'" class="info-icon" />
              {{ currentGenderFilter === 'F' ? $t('eventDashboard.female') : $t('eventDashboard.male') }}
            </span>
            <span v-if="currentTeamFilter" class="current-team">
              <font-awesome-icon icon="users" class="info-icon" />
              {{ currentTeamFilter }}
            </span>
            <span class="current-page">
              {{ $t('eventDashboard.page', { current: paginator.currentPage, total: paginator.totalPages }) }}
            </span>
          </div>
        </div>

        <!-- Desktop Table -->
        <div class="table-container mobile-table hide-on-mobile">
          <table class="results-table">
            <thead>
              <tr>
                <th class="rank-col">{{ $t('eventDashboard.number') }}</th>
                <th v-if="event.levels && (!event.rules || Object.keys(event.rules || {}).length > 1) || (event.rules && event.rules.length > 1)" class="level-col hide-mobile">{{ $t('eventDashboard.level') }}</th>
                <th class="avatar-col">
                  <font-awesome-icon icon="id-badge" class="header-icon" />
                </th>
                <th v-if="event.teams" class="team-col hide-mobile">{{ $t('eventDashboard.team') }}</th>
                <th class="points-col hide-mobile">{{ $t('eventDashboard.points') }}</th>
                <th class="actions-col">{{ $t('eventDashboard.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, _id) in athletes" :key="_id" class="athlete-row">
                <td class="rank-cell">
                  <div class="rank-number">
                    {{ (paginator.currentPage - 1) * paginator.limit + (_id + 1) }}
                  </div>
                  <!-- V2 Rule Indicators -->
                  <div v-if="event.v2_rule" class="rule-indicators">
                    <span class="penalty-badge" v-if="
                        (event.personal_target_point && (item.athlete.total_point < item.athlete.personal_target_point)) ||
                        ((event.personal_target_km || event.personal_target) && (item.athlete.total_run_km < item.athlete.personal_target_km))
                    ">
                      {{event.punishment_amount || "-50k"}}
                    </span>
                    <span class="penalty-badge" v-if="
                        event.personal_target_pace && !(event.total_point_target !== null && item.athlete.total_point >= event.total_point_target)
                    ">
                      -30k
                    </span>
                  </div>
                  <!-- V3 Rule Indicators -->
                  <div v-if="event.v3_rule" class="rule-indicators">
                    <span class="success-badge" v-if="
                      ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) ||
                      (event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                    ">
                      &#9989;
                    </span>
                    <span class="trophy-badge" v-if="
                      ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                        (event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                    ">
                      &#129409;
                    </span>
                    <span class="super-badge" v-if="
                      ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                        (event.total_days_target !== null && item.athlete.total_days > event.total_days_target)
                    ">
                      &#129417;
                    </span>
                    <span class="penalty-badge" v-if="
                               !((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                        !(event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                    ">
                      {{event.punishment_amount || "-50k"}}
                    </span>
                    <span class="pig-badge" v-if="
                      (event.total_km !== null && item.athlete.total_run_km == 0) ||
                      (item.athlete.personal_target && item.athlete.total_run_km == 0) ||
                        (event.total_days_target !== null && item.athlete.total_days == 0)
                    ">
                      &#128055;
                    </span>
                  </div>
                </td>
                <td v-if="event.levels && (!event.rules || (event.rules && event.rules.length > 1) || Object.keys(event.rules || {}).length > 1)" class="level-cell">
                  <div class="level-emoji">
                  <span
                    v-for="level in getAthleteLevels(item.athlete.personal_level)"
                    :key="level.content"
                    :class="level.class"
                  >
                    {{ level.content }}
                  </span>
                </div>
                </td>
                <td class="avatar-col-mobile">
                  <div class="avatar-stack">
                    <a :href="item.athlete.strava_link" target="_blank" class="avatar-link">
                      <img :src="item.athlete.profile" class="athlete-avatar" @error="$event.target.src = require('@/assets/default-running-avatar.png')" />
                    </a>
                    <div class="athlete-info-mobile">
                      <a :href="item.athlete.strava_link" target="_blank" class="athlete-name">
                        {{ item.athlete.firstname + ' ' + item.athlete.lastname }}
                      </a>
                      <div class="athlete-email">{{ item.athlete.email }}</div>
                      <div class="progress-cell-mobile">
                        <div v-if="event.total_km && !event.v3_rule" class="progress-container">
                          <div class="progress-bar-container">
                            <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/event.total_km)*100) + '%'"></div>
                          </div>
                          <div class="progress-text">
                            {{ 100 * (item.athlete.total_run_km / event.total_km) | formatNumber }}%
                          </div>
                        </div>
                        <div v-if="item.athlete.personal_target || item.athlete.personal_target_km || item.athlete.personal_target_point || item.athlete.personal_target_pace" class="progress-container">
                          <div v-if="item.athlete.personal_target" class="progress-container">
                            <div class="progress-bar-container">
                              <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/item.athlete.personal_target)*100) + '%'"></div>
                            </div>
                            <div class="progress-text">
                              {{ item.athlete.total_run_km | formatNumber }}/{{ item.athlete.personal_target }} km
                            </div>
                          </div>
                          <div v-if="item.athlete.personal_target_km" class="progress-container">
                            <div class="progress-bar-container">
                              <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/item.athlete.personal_target_km)*100) + '%'"></div>
                            </div>
                            <div class="progress-text">
                              {{ item.athlete.total_run_km | formatNumber }}/{{ item.athlete.personal_target_km }} km
                            </div>
                          </div>
                          <div v-if="item.athlete.personal_target_point" class="progress-container">
                            <div class="progress-bar-container">
                              <div class="progress-bar" :style="'width:' + ((item.athlete.total_point/item.athlete.personal_target_point)*100) + '%'"></div>
                            </div>
                            <div class="progress-text">
                              {{ item.athlete.total_point }}/{{ item.athlete.personal_target_point }} pts
                            </div>
                          </div>
                          <div v-if="item.athlete.personal_target_pace" class="pace-target">
                            {{item.athlete.personal_target_pace}} (+- 6s)
                          </div>
                        </div>
                        <div v-if="!item.athlete.personal_target && !item.athlete.personal_target_km && !item.athlete.personal_target_point && !item.athlete.personal_target_pace" class="total-km">
                          {{ item.athlete.total_run_km }}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td v-if="event.teams" class="team-cell hide-mobile">
                  <router-link :to="`/team-event-dashboard/${event.slug}`" class="team-link">
                    {{item.athlete.team.name}}
                  </router-link>
                </td>
                <td class="points-cell hide-mobile">
                  <div class="points-display">
                    <span v-if="event.rules" class="total-points">
                      {{ item.athlete.total_point }}
                    </span>
                    <span v-if="event.v3_rule" class="total-days">
                      / {{ item.athlete.total_days}}
                    </span>
                  </div>
                </td>
                <td class="actions-cell">
                  <div class="action-buttons">
                    <button @click="showAthleteModal(item)" class="action-btn inspect-btn">
                      <font-awesome-icon icon="search" class="btn-icon" />
                      <span class="action-text">{{ $t('eventDashboard.inspect') }}</span>
                    </button>
                    <button @click="goToProfile" class="action-btn ai-gen-btn" style="touch-action: manipulation;">
                      <font-awesome-icon icon="magic" class="btn-icon" />
                      <span class="action-text">AI Gen</span>
                    </button>
                    <button v-if="event.finished_cert" @click="getCert(event.id, item.athlete.id)" class="action-btn cert-btn" style="touch-action: manipulation;">
                      <font-awesome-icon icon="certificate" class="btn-icon" />
                      <span class="action-text">{{ $t('eventDashboard.cert') }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards -->
        <div class="mobile-results-cards show-on-mobile">
          <div
            v-for="(item, _id) in athletes"
            :key="_id"
            :class="['athlete-result-card', tableBgByGender]"
          >
            <div class="card-header">
              <div class="rank-badge">
                #{{ (paginator.currentPage - 1) * paginator.limit + (_id + 1) }}
              </div>
              <!-- Rule Indicators (copied from table row) -->
              <div class="rule-indicators" v-if="event.v2_rule">
                <span class="penalty-badge" v-if="
                    (event.personal_target_point && (item.athlete.total_point < item.athlete.personal_target_point)) ||
                    ((event.personal_target_km || event.personal_target) && (item.athlete.total_run_km < item.athlete.personal_target_km))
                ">
                  {{event.punishment_amount || "-50k"}}
                </span>
                <span class="penalty-badge" v-if="
                    event.personal_target_pace && !(event.total_point_target !== null && item.athlete.total_point >= event.total_point_target)
                ">
                  -30k
                </span>
              </div>
              <div class="rule-indicators" v-if="event.v3_rule">
                <span class="success-badge" v-if="
                  ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) ||
                  (event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                ">
                  &#9989;
                </span>
                <span class="trophy-badge" v-if="
                  ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                    (event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                ">
                  &#129409;
                </span>
                <span class="super-badge" v-if="
                  ((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                    (event.total_days_target !== null && item.athlete.total_days > event.total_days_target)
                ">
                  &#129417;
                </span>
                <span class="penalty-badge" v-if="
                           !((event.total_km != null && item.athlete.total_run_km >= event.total_km) || item.athlete.total_run_km >= item.athlete.personal_target) &&
                    !(event.total_days_target !== null && item.athlete.total_days >= event.total_days_target)
                ">
                  {{event.punishment_amount || "-50k"}}
                </span>
                <span class="pig-badge" v-if="
                  (event.total_km !== null && item.athlete.total_run_km == 0) ||
                  (item.athlete.personal_target && item.athlete.total_run_km == 0) ||
                    (event.total_days_target !== null && item.athlete.total_days == 0)
                ">
                  &#128055;
                </span>
              </div>
              <div class="card-header-actions">
                <button @click="showAthleteModal(item)" class="action-btn inspect-btn">
                  <font-awesome-icon icon="search" class="btn-icon" />
                  <span class="action-text">{{ $t('eventDashboard.inspect') }}</span>
                </button>
                <button @click="goToProfile" class="action-btn ai-gen-btn" style="touch-action: manipulation;">
                  <font-awesome-icon icon="magic" class="btn-icon" />
                  <span class="action-text">AI Gen</span>
                </button>
                <button v-if="event.finished_cert" @click="getCert(event.id, item.athlete.id)" class="action-btn cert-btn" style="touch-action: manipulation;">
                  <font-awesome-icon icon="certificate" class="btn-icon" />
                  <span class="action-text">{{ $t('eventDashboard.cert') }}</span>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="avatar-section" @click="showAthleteModal(item)" style="cursor:pointer;">
                <img :src="item.athlete.profile" class="athlete-avatar" @error="$event.target.src = require('@/assets/default-running-avatar.png')" />
                <div class="level-emoji">
                  <span
                    v-for="level in getAthleteLevels(item.athlete.personal_level)"
                    :key="level.content"
                    :class="level.class"
                  >
                    {{ level.content }}
                  </span>
                </div>
              </div>
              <div class="info-section">
                <div class="athlete-name">
                  <span>
                    {{ item.athlete.firstname + ' ' + item.athlete.lastname }}
                  </span>
                </div>
                <div class="athlete-email">{{ item.athlete.email }}</div>
                <div v-if="event.teams" class="athlete-team">
                  <router-link :to="`/team-event-dashboard/${event.slug}`">
                    {{ item.athlete.team.name }}
                  </router-link>
                </div>
                <div class="progress-section" :class="{ 'compact-progress': event.teams && isMobile }">
                  <!-- Show total points for mobile team view -->
                  <div v-if="event.teams && isMobile" class="total-points-display">
                    {{ item.athlete.total_point }} pts
                  </div>

                  <!-- Show progress bars for desktop or non-team events -->
                  <div v-else>
                    <!-- Copy progress bar logic from table row here -->
                    <div v-if="event.total_km && !event.v3_rule" class="progress-container">
                      <div class="progress-bar-container">
                        <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/event.total_km)*100) + '%'"></div>
                      </div>
                      <div class="progress-text">
                        {{ 100 * (item.athlete.total_run_km / event.total_km) | formatNumber }}%
                      </div>
                    </div>
                    <div v-if="item.athlete.personal_target || item.athlete.personal_target_km || item.athlete.personal_target_point || item.athlete.personal_target_pace" class="progress-container">
                      <div v-if="item.athlete.personal_target" class="progress-container">
                        <div class="progress-bar-container">
                          <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/item.athlete.personal_target)*100) + '%'"></div>
                        </div>
                        <div class="progress-text">
                          {{ item.athlete.total_run_km | formatNumber }}/{{ item.athlete.personal_target }} km
                        </div>
                      </div>
                      <div v-if="item.athlete.personal_target_km" class="progress-container">
                        <div class="progress-bar-container">
                          <div class="progress-bar" :style="'width:' + ((item.athlete.total_run_km/item.athlete.personal_target_km)*100) + '%'"></div>
                        </div>
                        <div class="progress-text">
                          {{ item.athlete.total_run_km | formatNumber }}/{{ item.athlete.personal_target_km }} km
                        </div>
                      </div>
                      <div v-if="item.athlete.personal_target_point" class="progress-container">
                        <div class="progress-bar-container">
                          <div class="progress-bar" :style="'width:' + ((item.athlete.total_point/item.athlete.personal_target_point)*100) + '%'"></div>
                        </div>
                        <div class="progress-text">
                          {{ item.athlete.total_point }}/{{ item.athlete.personal_target_point }} pts
                        </div>
                      </div>
                      <div v-if="item.athlete.personal_target_pace" class="pace-target">
                        {{item.athlete.personal_target_pace}} (+- 6s)
                      </div>
                    </div>
                    <div v-if="!item.athlete.personal_target && !item.athlete.personal_target_km && !item.athlete.personal_target_point && !item.athlete.personal_target_pace" class="total-km">
                      {{ item.athlete.total_run_km }}
                    </div>
                  </div>
                </div>
                <div class="points-section" v-if="!isMobile">
                  <span v-if="event.rules" class="total-points">
                    {{ item.athlete.total_point }}
                  </span>
                  <span v-if="event.v3_rule" class="total-days">
                    / {{ item.athlete.total_days}}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="pagination-card">
        <div class="pagination-controls">
          <button @click="gotoPage(paginator.currentPage-1)" class="pagination-btn prev-btn" :disabled="paginator.currentPage <= 1">
            <font-awesome-icon icon="arrow-left" class="btn-icon" />
            <span class="btn-text">{{ $t('eventDashboard.prev') }}</span>
          </button>

          <button @click="gotoPage(1)" class="pagination-btn home-btn" :disabled="paginator.currentPage === 1">
            <font-awesome-icon icon="home" class="btn-icon" />
            <span class="btn-text">{{ $t('eventDashboard.first') }}</span>
          </button>

          <button @click="gotoPage(paginator.currentPage+1)" class="pagination-btn next-btn" :disabled="paginator.currentPage >= paginator.totalPages">
            <span class="btn-text">{{ $t('eventDashboard.next') }}</span>
            <font-awesome-icon icon="arrow-right" class="btn-icon" />
          </button>
        </div>

        <div class="pagination-info">
          <span class="info-text">
            <font-awesome-icon icon="list-ol" class="info-icon" />
            {{ $t('eventDashboard.page', { current: paginator.currentPage, total: paginator.totalPages }) }}
          </span>
          <span class="total-count">
            <font-awesome-icon icon="users" class="info-icon" />
            {{ paginator.count }} {{ $t('eventDashboard.results') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Modal for Activity Details -->
    <b-modal size="lg" id="athlete-modal" @hide="resetAthleteStats" :title="$t('eventDashboard.activityDetails')" class="activity-modal" v-if="selectedAthlete">
      <div class="modal-content">
        <div class="athlete-summary">
          <div class="summary-card">
            <div class="athlete-header">
              <div class="athlete-info">
                <h4>{{ selectedAthlete.athlete.firstname + ' ' + selectedAthlete.athlete.lastname }}</h4>
                <p class="athlete-email">{{ selectedAthlete.athlete.email }}</p>
              </div>
              <div class="athlete-stats">
                <div class="stat-item">
                  <span class="stat-label">{{ $t('eventDashboard.totalPoints') }}:</span>
                  <span class="stat-value">{{ Number(selectedAthlete.athlete.total_point).toFixed(2) }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">{{ $t('eventDashboard.totalKm') }}:</span>
                  <span class="stat-value">{{ Number(selectedAthlete.athlete.total_run_km).toFixed(2) }}</span>
                </div>
                <div v-if="event.v3_rule" class="stat-item">
                  <span class="stat-label">{{ $t('eventDashboard.totalDays') }}:</span>
                  <span class="stat-value">{{ selectedAthlete.athlete.total_days }}</span>
                </div>
              </div>
            </div>
            <button v-if="event.v2_rule" @click="stats(selectedAthlete.athlete.activities)" class="stats-btn">             <font-awesome-icon icon="chart-pie" class="btn-icon" />
              {{ $t('eventDashboard.stats') }}
            </button>
          </div>

          <div v-if="showStatsSummary && athleteStats" class="stats-summary">
            <h4>{{ $t('eventDashboard.summaryByType', { count: getTotalActivities() }) }}</h4>
            <!-- Desktop Table -->
            <table class="stats-table desktop-stats-table">
              <thead>
                <tr>
                  <th>{{ $t('eventDashboard.type') }}</th>
                  <th>{{ $t('eventDashboard.name') }}</th>
                  <th>{{ $t('eventDashboard.date') }}</th>
                  <th>{{ $t('eventDashboard.km') }}</th>
                  <th>{{ $t('eventDashboard.pace') }}</th>
                  <th>{{ $t('eventDashboard.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stat, t) in athleteStats" :key="t">
                  <td>
                    <font-awesome-icon icon="swimmer" v-if="t=='Swim'" />
                    <font-awesome-icon icon="biking" v-if="t=='Ride' || t=='VirtualRide'" />
                    <font-awesome-icon icon="running" v-if="t=='Run' || t=='VirtualRun'" />
                    <font-awesome-icon icon="walking" v-if="t=='Walk'" />
                    <font-awesome-icon icon="puzzle-piece" v-if="t=='Brick2' || t=='Brick3'" />
                    <span style="margin-left:0.3em;">{{ t }}</span>
                  </td>
                  <td>{{ stat.name || t }}</td>
                  <td>{{ formatDate(stat.start_date_local) }}</td>
                  <td>{{ Math.round(stat.kms) }}</td>
                  <td>{{ formatPace(stat) }}</td>
                  <td class="action-buttons-cell">
                    <button
                      @click="inspectActivity(event.id, selectedAthlete.athlete.id, 'https://www.strava.com/activities/' + stat.id)"
                      class="action-btn-small inspect-btn"
                      title="$t('eventDashboard.viewOnStrava')"
                    >
                      <font-awesome-icon icon="search" />
                    </button>
                    <button
                      @click="report(event.id, selectedAthlete.athlete.id, 'https://www.strava.com/activities/' + stat.id)"
                      class="action-btn-small report-btn"
                      title="$t('eventDashboard.report')"
                    >
                      <font-awesome-icon icon="flag" />
                    </button>
                  </td>
                </tr>
                <tr class="stat-total">
                  <td><b>{{ $t('eventDashboard.total') }}</b></td>
                  <td><b>{{ getTotalActivities() }}</b></td>
                  <td><b>{{ getTotalKms() }}</b></td>
                  <td><b>{{ getTotalPoints() }}</b></td>
                  <td class="pct"><b>{{ getKmPercentage(getTotalKms()) }}%</b></td>
                  <td class="pct"><b>{{ getPointsPercentage(getTotalPoints()) }}%</b></td>
                </tr>
              </tbody>
            </table>
            <!-- Mobile Cards -->
            <div class="mobile-stats-cards">
              <div v-for="(stat, t) in athleteStats" :key="t" class="stat-card">
                <div class="stat-card-header">
                  <div class="stat-type">
                    <font-awesome-icon icon="swimmer" v-if="t=='Swim'" />
                    <font-awesome-icon icon="biking" v-if="t=='Ride' || t=='VirtualRide'" />
                    <font-awesome-icon icon="running" v-if="t=='Run' || t=='VirtualRun'" />
                    <font-awesome-icon icon="walking" v-if="t=='Walk'" />
                    <font-awesome-icon icon="puzzle-piece" v-if="t=='Brick2' || t=='Brick3'" />
                    <span>{{ t }}</span>
                  </div>
                  <div class="stat-count">{{ stat.count || 0 }} {{$t('eventDashboard.times')}}</div>
                </div>
                <div class="stat-card-body">
                  <div class="stat-row">
                    <span class="stat-label">{{ $t('eventDashboard.km') }}:</span>
                    <span class="stat-value">{{ Math.round(stat.kms) }}</span>
                    <span class="stat-percentage">({{ getKmPercentage(stat.kms) }}%)</span>
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">{{ $t('eventDashboard.points') }}:</span>
                    <span class="stat-value">{{ Number(stat.points).toFixed(2) }}</span>
                    <span class="stat-percentage">({{ getPointsPercentage(stat.points) }}%)</span>
                  </div>
                </div>
              </div>
              <!-- Total Card -->
              <div class="stat-card total-card">
                <div class="stat-card-header">
                  <div class="stat-type">
                    <font-awesome-icon icon="calculator" />
                    <span>{{ $t('eventDashboard.total') }}</span>
                  </div>
                  <div class="stat-count">{{ getTotalActivities() }} {{$t('eventDashboard.times')}}</div>
                </div>
                <div class="stat-card-body">
                  <div class="stat-row">
                    <span class="stat-label">{{ $t('eventDashboard.km') }}:</span>
                    <span class="stat-value">{{ getTotalKms() }}</span>
                    <span class="stat-percentage">({{ getKmPercentage(getTotalKms()) }}%)</span>
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">{{ $t('eventDashboard.points') }}:</span>
                    <span class="stat-value">{{ getTotalPoints() }}</span>
                    <span class="stat-percentage">({{ getPointsPercentage(getTotalPoints()) }}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="activities-table">
          <h5>{{ $t('eventDashboard.activityList') }}</h5>
          <table class="modern-compact-table">
            <thead>
              <tr>
                <th>{{ $t('eventDashboard.type') }}</th>
                <th class="hide-on-mobile">{{ $t('eventDashboard.name') }}</th>
                <th>{{ $t('eventDashboard.date') }}</th>
                <th>{{ $t('eventDashboard.km') }}</th>
                <th>{{ $t('eventDashboard.points') }}</th>
                <th class="hide-on-mobile">Blocks</th>
                <th class="hide-on-mobile">{{ $t('eventDashboard.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="act in selectedAthlete.athlete.activities" :key="act.id">
                <td>
                  <font-awesome-icon icon="swimmer" v-if="act.type=='Swim'" class="activity-icon swim"/>
                  <font-awesome-icon icon="biking" v-if="act.type=='Ride' || act.type=='VirtualRide'" class="activity-icon bike"/>
                  <font-awesome-icon icon="running" v-if="act.type=='Run' || act.type=='VirtualRun'" class="activity-icon run"/>
                  <font-awesome-icon icon="walking" v-if="act.type=='Walk'" class="activity-icon walk"/>
                  <font-awesome-icon icon="puzzle-piece" v-if="act.type=='Brick2' || act.type=='Brick3'" class="activity-icon brick"/>
                </td>
                <td class="hide-on-mobile">{{ act.name || act.type }}</td>
                <td>
                  <a :href="'https://www.strava.com/activities/' + act.id" target="_blank" rel="noopener noreferrer">
                    {{ act.start_date_local }}
                  </a>
                </td>
                <td>{{ act.km }}</td>
                <td>{{ act.point }}</td>
                <td class="hide-on-mobile">{{ act.block }}</td>
                <td class="hide-on-mobile action-buttons-cell">
                  <button
                    @click="inspectActivity(event.id, selectedAthlete.athlete.id, 'https://www.strava.com/activities/' + act.id)"
                    class="action-btn-small inspect-btn"
                    title="$t('eventDashboard.viewOnStrava')"
                  >
                    <font-awesome-icon icon="search" />
                  </button>
                  <button
                    @click="report(event.id, selectedAthlete.athlete.id, 'https://www.strava.com/activities/' + act.id)"
                    class="action-btn-small report-btn"
                    title="$t('eventDashboard.report')"
                  >
                    <font-awesome-icon icon="flag" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>

import $backend from '../backend'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faChartLine, faMapMarkedAlt } from '@fortawesome/free-solid-svg-icons'

library.add(faChartLine, faMapMarkedAlt)

export default {
  name: 'EventDashboard',
  metaInfo () {
    return {
      title: this.title
    }
  },
  data () {
    return {
      title: 'Event Dashboard',
      loadingEvent: true, // <-- add loading state for event

      // athletes: this.getAllActivities(),
      error: '',
      code: '',
      athletes: {},
      event: {
        rules: [],
        type: [],
        teams: [],
        levels: null,
        v2_rule: false,
        v3_rule: false,
        total_km: null,
        total_days_target: null,
        personal_target_point: null,
        personal_target_km: null,
        personal_target: null,
        personal_target_pace: null,
        total_point_target: null,
        punishment_amount: null,
        finished_cert: false,
        from: 0,
        to: 0,
        name: '',
        slug: '',
        image: '',
        id: null
      },
      athlete: {},
      athleteStats: {},
      selectedAthlete: null,
      isJoined: false,
      athleteNameSearchInput: '',
      filterByCond: {
        teams: [],
        levels: [],
        rules: []
      },
      filterByTeam: '',
      currentTeamName: '', // Add this to track the selected team name
      bgColorAndGenderCss: { F: 'female-rank-bg', M: 'male-rank-bg' },
      tableBgByGender: '',
      paginator: {
        currentPage: 1,
        totalPages: 1,
        limit: 10, // row per page todo: make this configurable
        count: 0
      },
      activitiesFields: ['type', 'distance', 'avg_pace', 'avg_speed', { key: 'id', label: 'Soii/Report' }],
      showStatsSummary: false,
      showMobileFilters: false // <-- add this
    }
  },
  created () {
    if (localStorage.hasOwnProperty('athlete')) {
      const athleteData = localStorage.getItem('athlete')
      if (athleteData && athleteData !== 'undefined') {
        try {
          this.athlete = JSON.parse(athleteData)
        } catch (e) {
          console.error('Error parsing athlete data:', e)
          localStorage.removeItem('athlete')
        }
      }
    }

    let slug = this.$route.params.slug
    this.getEvent(slug)
    this.title = this.title + ' | ' + slug
    this.loadingEvent = true // set loading true before fetching athletes
    this.getEventAthlete(slug)

    if (/^\?fbclid=/.test(location.search)) {
      location.replace(location.href.replace(/\?fbclid.+/, ''))
    }
  },
  methods: {
    rowClass (item, type) {
      if (!item || type !== 'row') return
      if (item.kind_of_activity === 'brick') return 'table-success'
    },
    isUpcomingEvent: function (fromTs) {
      return fromTs > Date.now()
    },
    isExpiredEvent: function (toTs) {
      return toTs < Date.now()
    },
    gotoPage (page) {
      if (page < 1) {
        page = 1
      }
      if (page > this.paginator.totalPages) {
        page = this.paginator.totalPages
      }
      let limit = this.paginator.limit
      let offset = (page - 1) * limit
      let slug = this.$route.params.slug
      $backend.getEventAthlete(slug, limit, offset, this.filterByCond).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.paginator.currentPage = page
        if (this.paginator.currentPage > this.paginator.totalPages) {
          this.paginator.currentPage = this.paginator.totalPages
        }

        let athleteID = this.athlete._id
        let isJoin = this.isJoined

        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          return
        }
        this.error = error.data ? error.data.msg : 'An error occurred'
      })
    },

    getEventAthlete (slug) {
      this.loadingEvent = true // set loading true before fetching athletes
      let limit = this.paginator.limit
      let offset = 0
      $backend.getEventAthlete(slug, limit, offset, this.filterByCond).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.loadingEvent = false // set loading false after athletes loaded
        let athleteID = this.athlete._id
        let isJoin = this.isJoined
        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          this.loadingEvent = false
          return
        }
        this.error = error.data ? error.data.msg : 'An error occurred'
        this.loadingEvent = false // set loading false on error
      })
    },
    getEvent (slug) {
      $backend.getEvent(slug)
        .then(response => {
          this.event = response.data.event
          this.$emit('event-loaded', this.event)
          if (this.event.v2_rule === true) {
            this.activitiesFields = [
              'type',
              {
                key: 'kind_of_activity',
                label: 'I/O'
              },
              {
                key: 'start_date_local',
                label: 'Date time'
              },

              'km',
              'block',
              'point',
              { key: 'id', label: 'Soii/Report' }
            ]
          }
          // Do not set loadingEvent here
        }).catch(error => {
          // Check if it's an unauthorized error - axios interceptor will handle redirect
          if (error && error.data && error.data.msg === 'unauthorized') {
            return
          }
          this.error = error.data ? error.data.msg : 'An error occurred'
          // Do not set loadingEvent here
        })
    },
    join: function (slug) {
      if (typeof this.athlete.token === 'undefined') {
        // Save current URL for redirect after successful login
        const currentUrl = this.$route.fullPath
        localStorage.setItem('redirectUrl', currentUrl)
        this.$router.push({ name: 'login' })
        return
      }
      $backend.join(this.athlete, slug, this.code)
        .then(response => {
          this.$router.push({ name: 'profile' })
        }).catch(error => {
          this.error = error.data.msg
          if (this.error === 'duplicated action') {
            this.error = '^.^'
          }
          if (this.error === 'code is required to join this event') {
            this.code = 'hãy nhập code /code is required to join this event'
          }
          if (this.error === 'code mismatch') {
            this.code = 'code sai /code mismatch'
          }
        })
    },
    goToProfile: function () {
      this.$router.push({ name: 'profile' })
    },
    filter: function (sex) {
      let limit = this.paginator.limit
      let offset = 0
      this.paginator.currentPage = 1
      let slug = this.$route.params.slug

      // Initialize filterByCond as an empty object
      this.filterByCond = {}

      // Add gender filter if selected and not undefined
      if (sex !== undefined && sex !== '') {
        this.filterByCond.athlete_sex = sex
        this.tableBgByGender = this.bgColorAndGenderCss[sex]
      } else {
        this.tableBgByGender = ''
      }

      // Add team filter if selected and not undefined
      if (this.filterByTeam !== undefined && this.filterByTeam !== '') {
        this.filterByCond.team_slug = this.filterByTeam
        // Find and store the team name
        if (this.event.teams) {
          const selectedTeam = this.event.teams.find(team => team.slug === this.filterByTeam)
          this.currentTeamName = selectedTeam ? selectedTeam.name : ''
        }
      } else {
        // When "All" option is selected or undefined, clear team name
        this.currentTeamName = ''
      }

      this.loadingEvent = true // set loading true before fetching athletes
      $backend.getEventAthlete(slug, limit, offset, this.filterByCond).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.loadingEvent = false // set loading false after athletes loaded
        let athleteID = this.athlete._id
        let isJoin = this.isJoined

        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          this.loadingEvent = false
          return
        }
        this.error = error.data ? error.data.msg : 'An error occurred'
        this.loadingEvent = false // set loading false on error
      })
    },
    searchByName (athleteNameSearchInput) {
      let limit = this.paginator.limit
      let offset = 0
      let slug = this.$route.params.slug
      // Clear team name when searching by name
      this.currentTeamName = ''
      this.filterByCond = {}
      this.tableBgByGender = ''
      this.loadingEvent = true // set loading true before searching
      $backend.searchAthleteInEvent(slug, athleteNameSearchInput, limit, offset).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.loadingEvent = false // set loading false after search
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          this.loadingEvent = false
          return
        }
        if (error.data && error.data.msg) {
          this.error = error.data.msg
        }
        this.loadingEvent = false // set loading false on error
      })
    },

    stats (activities) {
      if (this.showStatsSummary) {
        this.showStatsSummary = false
        return
      }
      let dict = {}
      if (this.event.type && Array.isArray(this.event.type)) {
        this.event.type.map((t) => {
          dict[t] = { blocks: 0, kms: 0, points: 0, count: 0 }
        })
      }

      activities.map((act) => {
        dict[act.type].blocks += act.block
        dict[act.type].kms += act.km
        dict[act.type].points += act.point
        dict[act.type].count += 1
      })
      this.athleteStats = dict
      this.showStatsSummary = true
    },

    resetAthleteStats () {
      this.athleteStats = {}
    },
    showAthleteModal (athlete) {
      this.selectedAthlete = athlete
      this.$nextTick(() => {
        this.$bvModal.show('athlete-modal')
      })
    },

    report (eventId, whoBeingReport, actLink) {
      alert('In sport we trust! Take it easy lah (^.^)')

      $backend.reportActivity(eventId, whoBeingReport, actLink, this.athlete).then(responseData => {
        alert(responseData.msg)
      }).catch(error => {
        if (error.data.msg) {
          alert(error.data.msg)
        }
      })
    },
    getCert (eventID, athleteID) {
      console.log('getCert called with:', eventID, athleteID)
      $backend.getCert(eventID, athleteID).then(resp => {
        console.log('Certificate API response:', resp)
        window.open('/api/cert/' + eventID + '/' + athleteID, '_blank')
      }).catch(err => {
        console.log('Certificate API error:', err)
      })
    },
    inspectActivity (eventId, whoBeingInspected, actLink) {
      $backend.inspectActivity(eventId, whoBeingInspected, actLink, this.athlete).then(responseData => {
        console.log(responseData.msg)
        window.open(actLink, '_blank')
      }).catch(error => {
        if (error.data.msg) {
          console.log(error.data.msg)
          // if (error.data.msg == 'unauthorized') {
          //  alert('Vui lòng đăng nhập để Soy :P')
          //  this.$router.push('/login')
          // }
        }
        window.open(actLink, '_blank')
      })
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d)) return dateStr
      const day = d.getDate().toString().padStart(2, '0')
      const month = (d.getMonth() + 1).toString().padStart(2, '0')
      const year = d.getFullYear()
      const hours = d.getHours().toString().padStart(2, '0')
      const minutes = d.getMinutes().toString().padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    },
    formatPace (act) {
      // Try avg_pace, then pace, else blank
      let pace = act.avg_pace || act.pace
      if (!pace) return ''
      // If pace is in seconds/km, format as mm:ss
      if (typeof pace === 'number') {
        const min = Math.floor(pace / 60)
        const sec = Math.round(pace % 60).toString().padStart(2, '0')
        return min + ':' + sec + ' /km'
      }
      // If pace is already a string
      return pace
    },
    formatPaceFromAvgPace (act) {
      // Format pace specifically from act.avg_pace in "mm:ss /km" format
      if (!act.avg_pace) return ''

      const pace = act.avg_pace

      // If pace is a number (decimal minutes format like 5.6 = 5min 36sec)
      if (typeof pace === 'number') {
        const totalMinutes = pace
        const min = Math.floor(totalMinutes)
        const sec = Math.round((totalMinutes - min) * 60).toString().padStart(2, '0')
        return min + ':' + sec + ' /km'
      }

      // If pace is already a string, try to convert it
      if (typeof pace === 'string') {
        // Remove any existing /km suffix and trim whitespace
        let cleanPace = pace.replace(/\/km$/i, '').trim()

        // Try to parse the string as a decimal number (minutes)
        const paceNum = parseFloat(cleanPace)
        if (!isNaN(paceNum)) {
          const totalMinutes = paceNum
          const min = Math.floor(totalMinutes)
          const sec = Math.round((totalMinutes - min) * 60).toString().padStart(2, '0')
          return min + ':' + sec + ' /km'
        }

        // If it's already in mm:ss format, just add /km if not present
        if (cleanPace.includes(':')) {
          return cleanPace + ' /km'
        }

        // If it's some other string format, return as-is
        return pace
      }

      // For any other type, return empty string
      return ''
    },
    getTotalActivities () {
      return Object.values(this.athleteStats).reduce((sum, stat) => sum + stat.count, 0)
    },
    getKmPercentage (kms) {
      const totalKms = this.getTotalKms()
      if (!totalKms) return '-'
      return ((kms / totalKms) * 100).toFixed(1)
    },
    getPointsPercentage (points) {
      const totalPoints = this.getTotalPoints()
      if (!totalPoints) return '-'
      return ((points / totalPoints) * 100).toFixed(1)
    },
    getTotalKms () {
      return Number(Object.values(this.athleteStats).reduce((sum, stat) => sum + stat.kms, 0).toFixed(2))
    },
    getTotalPoints () {
      return Number(Object.values(this.athleteStats).reduce((sum, stat) => sum + stat.points, 0).toFixed(2))
    },
    resetAllFilters () {
      // Reset all filter conditions
      this.filterByTeam = ''
      this.athleteNameSearchInput = ''
      this.filterByCond = {}
      this.currentTeamName = ''
      this.tableBgByGender = ''
      this.paginator.currentPage = 1

      // Reload data with reset filters
      let limit = this.paginator.limit
      let offset = 0
      let slug = this.$route.params.slug

      $backend.getEventAthlete(slug, limit, offset).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)

        let isJoin = this.isJoined
        let athleteID = this.athlete._id
        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        this.error = error.data.msg
      })
    },
    getAthleteLevels (personalLevel) {
      if (!personalLevel) return []

      const levels = []
      for (const [key, value] of Object.entries(this.levelMapping)) {
        if (personalLevel.includes(key)) {
          if (typeof value === 'string') {
            // Emoji level
            levels.push({ type: 'emoji', content: value })
          } else {
            // Percentage level with class
            levels.push({ type: 'percentage', content: value.text, class: value.class })
          }
        }
      }
      return levels
    }
  },
  computed: {
    now: function () {
      return new Date()
    },
    isMobile: function () {
      return window.innerWidth < 768
    },
    currentGenderFilter () {
      return this.filterByCond.athlete_sex || ''
    },
    currentTeamFilter () {
      return this.currentTeamName || ''
    },
    hasActiveFilters () {
      return (
        this.filterByTeam !== '' ||
        this.athleteNameSearchInput !== '' ||
        this.filterByCond.athlete_sex !== undefined ||
        Object.keys(this.filterByCond).length > 0
      )
    },
    levelMapping () {
      return {
        '1': '🐥',
        '2': '🐢',
        '3': '🐎',
        '4': '🦉',
        '5': '🐐',
        '6': '🦣',
        '7': '🐲',
        '8': '🦖',
        '9': '🦍',
        'Chanh': '🐉',
        'Sinh': '🐲',
        '30p': { text: '30p', class: 'level-30p' },
        '60p': { text: '60p', class: 'level-60p' },
        '70p': { text: '70p', class: 'level-70p' },
        '80p': { text: '80p', class: 'level-80p' },
        '90p': { text: '90p', class: 'level-90p' }
      }
    }
  },
  watch: {
    filterByTeam: function (newVal, oldVal) {
      // Trigger filter when team selection changes
      this.filter()
    }
  }
}

</script>

<style lang="scss">
// Main Container
.event-dashboard-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
  background: #f8f9fa;
  min-height: 100vh;
}

// Event Header Section
.event-header-section {
  margin-bottom: 2rem;
}

.event-header-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.event-header-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 2rem;
  flex-wrap: nowrap;
}

.event-basic-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.event-type-badge {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.type-icon {
  font-size: 1.25rem;
  padding: 0.5rem;
  border-radius: 50%;
  background: rgba(74, 144, 226, 0.1);
  color: #4a90e2;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.type-icon.swim {
  color: #00b4d8;
  background: rgba(0, 180, 216, 0.1);
}

.type-icon.bike {
  color: #06ffa5;
  background: rgba(6, 255, 165, 0.1);
}

.type-icon.run {
  color: #ff6b35;
  background: rgba(255, 107, 53, 0.1);
}

.type-icon.walk {
  color: #7209b7;
  background: rgba(114, 9, 183, 0.1);
}

.event-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.2;
}

.event-meta {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.event-dates {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.meta-icon {
  color: #4a90e2;
  font-size: 0.9rem;
}

.event-status {
  display: flex;
  gap: 0.5rem;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.status-badge.upcoming {
  background: linear-gradient(135deg, #00b4d8 0%, #0077b6 100%);
}

.status-badge.expired {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.status-badge.active {
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
}

.status-icon {
  font-size: 0.8rem;
}

.event-image {
  flex-shrink: 0;
}

.event-thumbnail {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  border: 3px solid #e9ecef;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.event-right-section {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-shrink: 0;
  flex-wrap: nowrap;
  min-width: fit-content;
}

.event-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  align-self: center;
  margin-bottom: 0;
  flex-shrink: 0;
  justify-content: flex-end;
}

.var-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    color: white;
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
  }
}

.var-icon {
  font-size: 1rem;
}

.map-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.9rem;
  background: linear-gradient(135deg, #ffb347 0%, #ffcc33 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 204, 51, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 204, 51, 0.4);
    color: white;
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
     padding: 0.25rem 0.5rem !important;
     font-size: 0.7rem !important;
     gap: 0.2rem !important;
     min-width: 38px !important;
     height: 38px !important;
     display: flex !important;
     align-items: center !important;
     justify-content: center !important;
  }
}

.map-icon {
  font-size: 1rem;
}

// Filters Section
.filters-section {
  margin-bottom: 1.5rem;
}

.filters-card compact-filters-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.filters-header compact-filters-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filters-title compact-filters-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.filters-icon {
  color: #4a90e2;
  font-size: 0.9rem;
}

.filters-content compact-filters-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: row;
  gap: 2rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-row compact-filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0;
  flex: 1 1 0;
  min-width: 0;
  &:last-child {
    margin-bottom: 0;
  }
}

.filter-label compact-filter-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  min-width: 80px;
  flex-shrink: 0;
}

.filter-icon {
  color: #4a90e2;
  font-size: 0.8rem;
}

.filter-controls compact-filter-controls {
  flex: 1;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.filter-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #6c757d;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &.active {
    background: #4a90e2;
    color: #fff;
    border-color: #4a90e2;
    box-shadow: 0 2px 8px rgba(74,144,226,0.10);
    font-weight: 700;
    transform: none;
  }

  &:not(.active) {
    opacity: 0.85;
    background: #fff;
    color: #6c757d;
    border-color: #e9ecef;
    font-weight: 500;
  }
}

.overall-btn {
  &:hover {
    background: #6c757d;
    color: white;
    border-color: #6c757d;
  }
}

.female-btn {
  &:hover {
    background: #e91e63;
    color: white;
    border-color: #e91e63;
  }
}

.male-btn {
  &:hover {
    background: #2196f3;
    color: white;
    border-color: #2196f3;
  }
}

.team-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.85rem;
  background: white;
  min-width: 150px;
  color: #2c3e50;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }
}

.search-wrapper {
  display: flex;
  gap: 0.5rem;
  flex: 1;
  max-width: 300px;
}

.search-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.85rem;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }

  &::placeholder {
    color: #adb5bd;
  }
}

.search-btn {
  padding: 0.5rem 0.75rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8rem;

  &:hover {
    background: #357abd;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

// Results Section
.results-section {
  margin-bottom: 2rem;
}

.results-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.results-header {
  padding: 2rem;
  border-bottom: 1px solid #f1f3f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.results-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.results-icon {
  color: #4a90e2;
}

.results-info {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.total-participants,
.current-team,
.current-page,
.current-gender {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-weight: 500;
}

.current-team {
  color: #4a90e2;
  font-weight: 600;
  background: rgba(74, 144, 226, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  border: 1px solid rgba(74, 144, 226, 0.2);
}

.current-gender {
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  border: 1px solid;
}

.current-gender.female-filter {
  color: #e91e63;
  background: rgba(233, 30, 99, 0.1);
  border-color: rgba(233, 30, 99, 0.2);
}

.current-gender.male-filter {
  color: #2196f3;
  background: rgba(33, 150, 243, 0.1);
  border-color: rgba(33, 150, 243, 0.2);
}

.info-icon {
  color: #4a90e2;
}

// Table Styles
.table-container {
  overflow-x: auto;
}

.results-table {
  width: 100%;
  border-collapse: collapse;

  th {
    background: #f8f9fa;
    padding: 1rem;
    text-align: center;
    font-weight: 600;
    color: #2c3e50;
    border-bottom: 2px solid #e9ecef;
  }

  td {
    padding: 1rem;
    border-bottom: 1px solid #f1f3f4;
    vertical-align: middle;
  }

  tbody tr {
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.rank-cell {
  text-align: center;
  min-width: 80px;
}

.rank-number {
  font-size: 1.2rem;
  font-weight: 700;
  color: #4a90e2;
}

.rule-indicators {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.penalty-badge {
  background: #f8d7da;
  color: #721c24;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.success-badge {
  background: #d4edda;
  color: #155724;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.trophy-badge {
  background: #fff3cd;
  color: #856404;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.super-badge {
  background: #cce5ff;
  color: #004085;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pig-badge {
  background: #f8d7da;
  color: #721c24;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.level-cell {
  text-align: center;
}

.level-emoji {
  font-size: 1.5rem;
}

.avatar-cell {
  text-align: center;
}

.avatar-link {
  display: inline-block;
  border-radius: 50%;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.athlete-avatar {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 50%;
  border: 3px solid #e9ecef;
}

.name-cell {
  min-width: 200px;
}

.athlete-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.athlete-name {
  font-weight: 600;
  color: #2c3e50;
  text-decoration: none;

  &:hover {
    color: #4a90e2;
  }
}

.athlete-email {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.team-cell {
  text-align: center;
}

.team-link {
  color: #4a90e2;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.progress-cell {
  min-width: 150px;
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-bar-container {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.85rem;
  color: #7f8c8d;
  font-weight: 500;
}

.pace-target {
  font-size: 0.85rem;
  color: #2c3e50;
  font-weight: 500;
}

.total-km {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.points-cell {
  text-align: center;
}

.points-display {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.total-points {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}

.total-days {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.actions-cell {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.inspect-btn {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #138496 0%, #117a8b 100%);
  }
}

.cert-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  position: relative;
  z-index: 10;

  &:hover {
    background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
  }

  &:active {
    transform: scale(0.95);
  }
}

.ai-gen-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  z-index: 10;

  &:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    color: white !important;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
  }

  .btn-icon {
    color: #ffd700;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
    animation: sparkle 2s ease-in-out infinite;
  }

  .action-text {
    color: white;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
}

/* Sparkle animation for magic icon */
@keyframes sparkle {
  0%, 100% {
    transform: scale(1);
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
  }
  50% {
    transform: scale(1.1);
    text-shadow: 0 0 15px rgba(255, 215, 0, 0.8);
  }
}

// Pagination
.pagination-section {
  margin-bottom: 2rem;
}

.pagination-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #4a90e2;
  background: #4a90e2;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;

  &:hover:not(:disabled) {
    background: #357abd;
    border-color: #357abd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  }

  &:disabled {
    background: #e9ecef;
    border-color: #e9ecef;
    color: #adb5bd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.btn-icon {
  font-size: 0.8rem;
}

.btn-text {
  font-weight: 600;
}

.home-btn {
  background: #28a745;
  border-color: #28a745;

  &:hover:not(:disabled) {
    background: #218838;
    border-color: #218838;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
}

.pagination-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.info-text,
.total-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-weight: 500;
  font-size: 0.9rem;
}

.info-icon {
  color: #4a90e2;
  font-size: 0.8rem;
}

// Modal Styles
.activity-modal {
  .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  }

  .modal-header {
    border-bottom: 1px solid #f1f3f4;
    padding: 1.5rem 2rem;
  }

  .modal-body {
    padding: 2rem;
  }
}

.athlete-summary {
  margin-bottom: 2rem;
}

.summary-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1rem;
}

.athlete-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.athlete-info h4 {
  margin-bottom: 0.25rem;
}

.athlete-email {
  font-size: 0.9rem;
  color: #7f8c8d;
}

.athlete-stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5em 0.75em;
  border-radius: 6px;
  background: #f8f9fa;
  text-align: center;
  border: 1px solid #e9ecef;
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0.25rem;
}

.stat-type {
  font-size: 0.8rem;
  color: #7f8c8d;
  font-weight: 500;
}

.stat-count {
  font-size: 0.8rem;
  color: #2c3e50;
  font-weight: 600;
}

.stat-values {
  display: flex;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
}

.stat-km {
  color: #00b4d8;
}

.stat-points {
  color: #28a745;
}

.stat-percentages {
  display: flex;
  gap: 0.5rem;
  font-size: 0.7rem;
  color: #7f8c8d;
  margin-top: 0.25rem;
}

.stat-km-pct, .stat-points-pct {
  font-weight: 500;
}

.activities-table {
  .modern-table {
    th {
      background: #f8f9fa;
      font-weight: 600;
      color: #2c3e50;
    }

    td {
      vertical-align: middle;
    }
  }
}

.activity-icon {
  font-size: 1.2rem;

  &.swim { color: #00b4d8; }
  &.bike { color: #06ffa5; }
  &.run { color: #ff6b35; }
  &.walk { color: #7209b7; }
  &.brick { color: #6f42c1; }
  &.outdoor { color: #28a745; }
  &.indoor { color: #6c757d; }
}

.brick-label {
  font-weight: 600;
  color: #6f42c1;
  margin-right: 0.25rem;
}

.activity-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

/* Button container alignment for desktop */
@media (min-width: 768px) {
  .action-buttons-cell {
    text-align: right;
  }
}

.action-btn-small {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
  }

  &.inspect-btn {
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
    color: white;
  }

  &.report-btn {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
  }
}

// Gender-based backgrounds
.male-rank-bg {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.035) 0%, rgba(120, 180, 255, 0.07) 100%);
  /* Extra light blue pastel */
}

.female-rank-bg {
  background: linear-gradient(135deg, rgba(233, 30, 99, 0.035) 0%, rgba(255, 170, 210, 0.07) 100%);
  /* Extra light pink pastel */
}

// Mobile status badges
.mobile-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  }
}

.mobile-status-icon {
  font-size: 0.9rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-status-badge.upcoming {
  background: linear-gradient(135deg, #00b4d8 0%, #0077b6 100%);
}

.mobile-status-badge.expired {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.mobile-status-badge.active {
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
}

// Show/hide classes for mobile responsiveness
.show-on-mobile {
  display: none;
}

// Responsive Design
@media (max-width: 768px) {
  .show-on-mobile {
    display: inline-flex !important;
  }

  .hide-on-mobile {
    display: none !important;
  }

  .event-dashboard-container {
    padding: 1rem;
  }

  .event-header-card {
    padding: 0.5rem 0.5rem !important;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .event-header-content {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start;
    padding: 0.3rem !important;
    gap: 0.5rem !important;
    text-align: left !important;
  }
  .event-basic-info {
    flex: 1;
    flex-direction: row !important;
    align-items: center !important;
    gap: 0.3rem !important;
    display: flex !important;
    min-width: 0;
    flex-wrap: nowrap !important;
    margin-right: 0.5rem !important;
  }
  .event-type-badge {
    gap: 0.1rem;
    font-size: 0.75rem;
    margin-right: 0.2em;
    display: flex !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
  }
  .event-name {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0.3em 0 0;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 90px;
  }
  .event-meta {
    flex-direction: row !important;
    align-items: center !important;
    gap: 0.2rem !important;
    font-size: 0.75rem;
    margin: 0;
    flex-wrap: nowrap !important;
    display: flex !important;
  }
  .event-dates {
    display: none !important;
  }
  .event-status {
    gap: 0.1rem;
    margin-left: 0.2em;
    display: flex !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
  }
  .status-badge {
    padding: 0.2rem 0.5rem;
    font-size: 0.65rem;
  }

  .mobile-status-badge {
    width: 24px;
    height: 24px;
    border-width: 1.5px;
  }

  .mobile-status-icon {
    font-size: 0.8rem;
  }

  .type-icon {
    font-size: 0.9rem !important;
    padding: 0.3rem !important;
  }
  .event-image {
    flex-shrink: 0;
    align-self: center !important;
    margin-left: 0.5em;
  }
  .event-thumbnail {
    width: 38px;
    height: 38px;
    border-radius: 7px;
    border-width: 2px;
  }

  .event-right-section {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 1.5rem !important;
  flex-shrink: 0 !important;
  min-width: 120px !important;
}

  .event-actions {
    flex-shrink: 0 !important;
    gap: 0.5rem !important;
    align-self: flex-start !important;
    margin-bottom: 0 !important;
    justify-content: center !important;
    margin-left: 0 !important;
    padding-right: 0 !important;
    margin-top: 0.2rem !important;
  }

  .var-btn {
    padding: 0.4rem 0.6rem !important;
    font-size: 0.7rem !important;
    gap: 0.2rem !important;
    min-width: 38px !important;
    height: 38px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .var-icon {
    font-size: 0.8rem !important;
  }

  .filters-content {
    padding: 1rem;
    flex-direction: column;
    gap: 0;
  }

  .filter-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .filter-label {
    min-width: auto;
  }

  .filter-controls {
    width: 100%;
  }

  .filter-btn {
    flex: 1;
    text-align: center;
  }

  .search-wrapper {
    max-width: none;
  }

  .results-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .results-info {
    flex-direction: column;
    gap: 0.5rem;
  }

  .current-team {
    font-size: 0.85rem;
    padding: 0.2rem 0.5rem;
  }

  .pagination-controls {
    flex-direction: row;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-numbers {
    order: 2;
  }

  .pagination-btn {
    min-width: 60px;
    padding: 0.5rem 0.8rem;
    font-size: 0.8rem;
  }

  .pagination-btn .btn-text {
    display: none;
  }

  .pagination-btn {
    min-width: 40px;
    padding: 0.5rem;
    justify-content: center;
  }

  .pagination-info {
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .results-table {
    th, td {
      padding: 0.75rem 0.5rem;
      font-size: 0.9rem;
    }
  }

  .athlete-avatar {
    width: 40px;
    height: 40px;
  }

  .action-buttons {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .results-section,
  .results-card {
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: none !important;
    overflow: visible !important;
  }
  .mobile-table,
  .table-container {
    overflow-x: auto !important;
    width: 100vw !important;
    max-width: 100vw !important;
    min-width: 0 !important;
    -webkit-overflow-scrolling: touch;
  }
  .results-table {
    min-width: 450px !important;
    table-layout: auto !important;
    border-radius: 0;
    th, td {
      padding: 0.15rem 0.05rem;
      font-size: 0.7rem;
    }
    th.rank-col, td.rank-cell {
      min-width: 24px !important;
      max-width: 28px !important;
      width: 24px !important;
      padding-left: 0.1rem !important;
      padding-right: 0.1rem !important;
      font-size: 0.7rem !important;
    }
    th.actions-col, td.actions-cell {
      min-width: 40px !important;
      max-width: 48px !important;
      width: 40px !important;
      padding-left: 0.1rem !important;
      padding-right: 0.1rem !important;
      font-size: 0.7rem !important;
    }
  }
  .hide-mobile {
    display: none !important;
  }
  .avatar-col-mobile {
    min-width: 80px;
    max-width: 90px;
    padding: 0.15rem 0.05rem;
  }
  .avatar-stack {
    display: flex;
    align-items: flex-start;
    gap: 0.1rem;
  }
  .athlete-info-mobile {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
    flex: 1;
  }
  .athlete-name, .athlete-email {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }
  .progress-cell-mobile {
    margin-top: 0.05rem;
    margin-bottom: 0.05rem;
  }
  .progress-text {
    font-size: 0.65rem;
  }
  .action-buttons {
    flex-direction: column;
    gap: 0.1rem;
    width: 100%;
  }
  .action-btn, .cert-btn, .inspect-btn, .ai-gen-btn {
    width: 100%;
    min-width: 0;
    font-size: 0.7rem;
    padding: 0.4rem 0.2rem;
    justify-content: center;
    min-height: 32px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
  .athlete-avatar {
    width: 20px;
    height: 20px;
  }
  .action-text {
    display: none !important;
  }
  .actions-col, .actions-cell {
    display: table-cell !important;
  }
}

@media (max-width: 480px) {
  .results-table {
    th, td {
      padding: 0.2rem 0.1rem;
      font-size: 0.7rem;
    }
    th.rank-col, td.rank-cell {
      min-width: 18px !important;
      max-width: 22px !important;
      width: 18px !important;
      padding-left: 0.05rem !important;
      padding-right: 0.05rem !important;
      font-size: 0.65rem !important;
    }
    th.actions-col, td.actions-cell {
      min-width: 28px !important;
      max-width: 36px !important;
      width: 28px !important;
      padding-left: 0.05rem !important;
      padding-right: 0.05rem !important;
      font-size: 0.65rem !important;
    }
  }
  .avatar-col-mobile {
    min-width: 100px;
    max-width: 120px;
    padding: 0.2rem 0.1rem;
  }
  .athlete-name, .athlete-email {
    max-width: 150px;
  }
  .athlete-avatar {
    width: 26px;
    height: 26px;
  }
  .action-btn, .cert-btn, .inspect-btn, .ai-gen-btn {
    font-size: 0.75rem;
    padding: 0.5rem 0.3rem;
    min-height: 36px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
}

@media (max-width: 375px) {
  .action-btn, .cert-btn, .inspect-btn, .ai-gen-btn {
    min-height: 44px;
    padding: 0.6rem 0.4rem;
    font-size: 0.8rem;
    border-radius: 8px;
    margin: 0.1rem 0;
  }

  .cert-btn {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
    border: 2px solid #28a745;
  }

  .ai-gen-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
    border: 2px solid #667eea;
  }

  .btn-icon {
    font-size: 1rem !important;
  }
}

@media (max-width: 320px) {
  .action-btn, .cert-btn, .inspect-btn, .ai-gen-btn {
    min-height: 48px;
    padding: 0.8rem 0.5rem;
    font-size: 0.9rem;
    border-radius: 10px;
    margin: 0.2rem 0;
  }

  .cert-btn {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
    box-shadow: 0 3px 6px rgba(40, 167, 69, 0.4);
    border: 3px solid #28a745;
  }

  .ai-gen-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    box-shadow: 0 3px 6px rgba(102, 126, 234, 0.4);
    border: 3px solid #667eea;
  }

  .btn-icon {
    font-size: 1.2rem !important;
  }
}

// Smooth transitions
* {
  transition-property: color, background-color, transform, box-shadow, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-compact-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.93em;
  background: #fff;
}
.modern-compact-table th, .modern-compact-table td {
  padding: 0.32em 0.5em;
  border-bottom: 1px solid #ececec;
  text-align: left;
  vertical-align: middle;
}
.modern-compact-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}
.modern-compact-table tr:hover {
  background: #f6f8fa;
}
.modern-compact-table td {
  color: #222;
}
.stat-total {
  background: #e9ecef;
  font-weight: 700;
  border: 2px solid #b0b0b0;
}
.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95em;
  margin-top: 0.5rem;
  background: #fff;
}
.stats-table th, .stats-table td {
  padding: 0.3em 0.5em;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
}
.stats-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}
.stats-table tr.stat-total {
  background: #e9ecef;
  font-weight: 700;
  border-top: 2px solid #b0b0b0;
}
.stats-table .pct {
  color: #007bff;
  font-weight: 600;
}
// Responsive for stats summary cards
.mobile-stats-cards {
  display: none;
}
@media (max-width: 700px) {
  .desktop-stats-table {
    display: none;
  }
  .mobile-stats-cards {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  }
  .stat-card {
    background: #f8f9fa;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .stat-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }
  .stat-type {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 1.1em;
  }
  .stat-count {
    font-size: 0.95em;
    color: #4a90e2;
    font-weight: 600;
  }
  .stat-card-body {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .stat-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.98em;
  }
  .stat-label {
    color: #888;
    min-width: 40px;
  }
  .stat-value {
    font-weight: 700;
    color: #2c3e50;
  }
  .stat-percentage {
    color: #007bff;
    font-weight: 600;
    font-size: 0.95em;
  }
  .total-card {
    background: #e9ecef;
    border: 2px solid #b0b0b0;
  }
}

// Mobile results cards
.mobile-results-cards {
  display: none;
}
@media (max-width: 700px) {
  .hide-on-mobile {
    display: none !important;
  }
  .show-on-mobile {
    display: block !important;
  }
  .mobile-results-cards {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin: 0.5rem 0;
  }
  .athlete-result-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.10);
    padding: 0.4rem 0.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    border-left: 5px solid #4a90e2; // blue accent
    transition: box-shadow 0.2s, border-color 0.2s;
    position: relative;
  }
  // Zebra striping for cards
  .athlete-result-card:nth-child(even) {
    background: #f6f8fa;
    border-left-color: #28a745; // green accent for even rows
  }
  .athlete-result-card:hover {
    box-shadow: 0 4px 16px rgba(74,144,226,0.18);
    border-left-color: #ff6b35; // orange on hover
  }
  .card-header {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    margin-bottom: 0.03rem;
    flex-direction: row;
  }
  .card-header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.2rem;
  }
  .rank-badge {
    background: #4a90e2;
    color: #fff;
    border-radius: 50%;
    width: 1.7em;
    height: 1.7em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.95em;
    box-shadow: 0 2px 8px rgba(74,144,226,0.15);
    flex-shrink: 0;
  }
  .card-header .rule-indicators {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.15rem;
    margin-top: 0;
    margin-left: 0.3em;
    min-height: 1.7em;
  }
  .penalty-badge,
  .success-badge,
  .trophy-badge,
  .super-badge,
  .pig-badge {
    display: flex;
    align-items: center;
    height: 1.5em;
    font-size: 0.85em;
  }
  .card-body {
    display: flex;
    gap: 0.4rem;
    align-items: flex-start;
  }
  .avatar-section {
    flex-shrink: 0;
  }
  .athlete-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1.5px solid #e9ecef;
  }
  .info-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    align-items: flex-end;
    text-align: right;
  }
  .athlete-name a {
    font-weight: 600;
    color: #2c3e50;
    text-decoration: none;
    font-size: 0.95em;
  }
  .athlete-email {
    font-size: 0.8em;
    color: #7f8c8d;
  }
  .athlete-team a {
    color: #4a90e2;
    font-size: 0.85em;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    padding: 0.4rem 0.8rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 0.6rem;
    border: 1.5px solid #e0e0e0;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    min-height: 32px;
    font-weight: 500;
  }

  .athlete-team a:hover {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-color: #4a90e2;
    box-shadow: 0 3px 8px rgba(74, 144, 226, 0.2);
    transform: translateY(-1px);
    color: #4a90e2;
  }

  .athlete-team a:active {
    background: linear-gradient(135deg, #d1e7ff 0%, #a8d4ff 100%);
    transform: scale(0.98);
  }
  .progress-section {
    margin-top: 0.03em;
    margin-bottom: 0.03em;
  }
  .points-section {
    font-size: 0.9em;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0.05em;
  }
  .card-actions {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.4em;
    justify-content: flex-end;
  }
  .action-btn {
    flex: 1;
    min-width: 0;
    font-size: 0.8em;
    padding: 0.25em 0.1em;
    justify-content: center;
    min-height: 22px;
  }
  .action-text {
    display: none;
  }
}

@media (max-width: 700px) {
  // ... existing code ...
  .progress-text {
    font-size: 1.1em; // Make it larger on mobile
    font-weight: 700;
    color: #357abd;
  }

  // Compact athlete result card for mobile
  .athlete-result-card {
    padding: 0.25rem 0.4rem;
    gap: 0.08rem;
    border-radius: 8px;
  }

  .card-header {
    gap: 0.15rem;
    margin-bottom: 0.01rem;
  }

  .rank-badge {
    width: 1.5em;
    height: 1.5em;
    font-size: 0.85em;
  }

  .card-body {
    gap: 0.25rem;
  }

  .athlete-avatar {
    width: 28px;
    height: 28px;
  }

  .info-section {
    gap: 0.03rem;
    align-items: flex-end;
    text-align: right;
  }

  .athlete-name a {
    font-size: 0.9em;
  }

  .athlete-email {
    font-size: 0.75em;
  }

  .athlete-team a {
    font-size: 0.8em;
    padding: 0.35rem 0.7rem;
    min-height: 28px;
    background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
    border: 1.5px solid #4a90e2;
    box-shadow: 0 2px 6px rgba(74, 144, 226, 0.15);
  }

  .athlete-team a:hover {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    box-shadow: 0 3px 10px rgba(74, 144, 226, 0.25);
  }

  .progress-section {
    margin-top: 0.01em;
    margin-bottom: 0.01em;
  }

  .points-section {
    font-size: 0.85em;
    margin-top: 0.03em;
  }

  .card-actions {
    gap: 0.25rem;
    margin-top: 0.25em;
  }

  .action-btn {
    font-size: 0.75em;
    padding: 0.2em 0.05em;
    min-height: 20px;
  }

  // ... existing code ...
}

.mobile-filter-toggle-btn {
  display: none;
  width: 100%;
  background: #4a90e2;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 0.45em 0.7em;
  font-size: 0.98em;
  font-weight: 600;
  margin-bottom: 0.7em;
  box-shadow: 0 2px 8px rgba(74,144,226,0.10);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5em;
  cursor: pointer;
  transition: background 0.2s;
  min-height: 36px;
  min-width: 0;
}
.mobile-filter-toggle-btn:active,
.mobile-filter-toggle-btn:focus {
  background: #357abd;
}
.mobile-filter-toggle-btn .filters-icon.compact-icon {
  font-size: 1.1em;
  margin-right: 0.3em;
}
.mobile-filter-toggle-btn .toggle-label {
  font-size: 0.98em;
  font-weight: 600;
}
.mobile-filter-toggle-btn .chevron-icon {
  font-size: 1em;
  margin-left: auto;
  transition: transform 0.3s;
}
.mobile-filter-toggle-btn .chevron-icon.rotated {
  transform: rotate(180deg);
}
@media (max-width: 768px) {
  .mobile-filter-toggle-btn {
    display: flex;
  }
  .filters-section.mobile-hidden {
    display: none !important;
  }
}

.filters-fade-slide-enter-active, .filters-fade-slide-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.filters-fade-slide-enter, .filters-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}

.compact-filter-btn {
  transition: background 0.18s, color 0.18s, transform 0.18s;
  background: #f4f6fa;
  color: #4a90e2;
  border: 1px solid #e0e6ef;
  box-shadow: none;
  outline: none;
  &:hover, &:focus {
    background: #e3f0fc;
    color: #1565c0;
    transform: scale(1.08);
    border-color: #90caf9;
    z-index: 1;
  }
  &.active {
    background: #4a90e2;
    color: #fff;
    border-color: #4a90e2;
    font-weight: 700;
    transform: none;
  }
}

.compact-team-select {
  transition: background 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s, transform 0.18s;
  background: #f4f6fa;
  color: #4a90e2;
  border: 1px solid #e0e6ef;
  outline: none;
  &:hover, &:focus {
    background: #e3f0fc;
    color: #1565c0;
    border-color: #90caf9;
    box-shadow: 0 0 0 2px #e3f0fc;
    transform: scale(1.04);
    z-index: 1;
  }
}

.compact-search-btn {
  transition: background 0.18s, color 0.18s, transform 0.18s;
  background: #f4f6fa;
  color: #4a90e2;
  border: 1px solid #e0e6ef;
  outline: none;
  &:hover, &:focus {
    background: #e3f0fc;
    color: #1565c0;
    border-color: #90caf9;
    transform: scale(1.08);
    z-index: 1;
  }
}

.compact-search-input {
  transition: background 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s, transform 0.18s;
  background: #f4f6fa;
  color: #2c3e50;
  border: 1px solid #e0e6ef;
  outline: none;
  &:hover, &:focus {
    background: #e3f0fc;
    color: #1565c0;
    border-color: #90caf9;
    box-shadow: 0 0 0 2px #e3f0fc;
    transform: scale(1.04);
    z-index: 1;
  }
}

.compact-reset-btn {
  transition: background 0.18s, color 0.18s, transform 0.18s;
  background: #f4f6fa;
  color: #e74c3c;
  border: 1px solid #e0e6ef;
  outline: none;
  &:hover, &:focus {
    background: #fdf2f2;
    color: #c0392b;
    border-color: #f5b7b1;
    transform: scale(1.08);
    z-index: 1;
  }
}

// Compact progress section for mobile when event has teams
.compact-progress {
  .progress-container {
    gap: 0.2rem;
    margin-bottom: 0.1rem;
  }

  .progress-bar-container {
    height: 6px;
    border-radius: 3px;
  }

  .progress-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a90e2;
    margin-top: 0.1rem;
  }

  .pace-target {
    font-size: 0.75rem;
    font-weight: 600;
    color: #2c3e50;
  }

  .total-km {
    font-size: 0.85rem;
    font-weight: 600;
    color: #2c3e50;
  }

  .total-points-display {
    font-size: 0.9rem;
    font-weight: 700;
    color: #2c3e50;
    text-align: center;
    padding: 0.2rem 0;
  }
}

.running-animate {
  animation: run-bounce 1s infinite linear;
}
@keyframes run-bounce {
  0% { transform: translateY(0); }
  20% { transform: translateY(-8px); }
  40% { transform: translateY(0); }
  100% { transform: translateY(0); }
}

// Percentage level badges styling
.level-emoji span[class*="p"] {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.4rem;
  border-radius: 12px;
  margin: 0.1rem;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.level-emoji span[class*="p"]:before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.level-emoji span[class*="p"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.level-emoji span[class*="p"]:hover:before {
  left: 100%;
}

// Specific percentage colors using classes
.level-emoji .level-30p {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  box-shadow: 0 2px 4px rgba(255, 154, 158, 0.3);
}

.level-emoji .level-60p {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  box-shadow: 0 2px 4px rgba(168, 237, 234, 0.3);
}

.level-emoji .level-70p {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  box-shadow: 0 2px 4px rgba(255, 236, 210, 0.3);
}

.level-emoji .level-80p {
  background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
  box-shadow: 0 2px 4px rgba(255, 154, 158, 0.3);
}

.level-emoji .level-90p {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

// Mobile responsive adjustments
@media (max-width: 768px) {
  .level-emoji span[class*="p"] {
    font-size: 0.65rem;
    padding: 0.15rem 0.3rem;
    margin: 0.05rem;
  }
}

@media (max-width: 480px) {
  .level-emoji span[class*="p"] {
    font-size: 0.6rem;
    padding: 0.1rem 0.25rem;
    margin: 0.03rem;
  }
}
</style>
